import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { config } from './config';
import { AdminApi } from './api/admin';
import { FakeAuthProvider } from './auth/fake';
import type { AuthProvider } from './auth/provider';
import { App } from './ui/App';
import { DEVICE_LOCALE, translatorFor } from './messages';
// The console's type, from the bundle rather than a font CDN (the redesign
// plan §2): the four Plex Sans weights the screens use and the two Plex Mono
// weights ids and codes use — nothing else is shipped. Imported before the
// console's own sheet so its `font-family` finds them already declared.
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-sans/700.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import './styles.css';

/**
 * Boot.
 *
 * Shorter than `apps/app`'s by everything that app needs and this one does not:
 * no IndexedDB to open before the first paint, no service worker to register,
 * no sync loop to start. The console is online-first by design — an operator
 * seat has no basement story — so booting is: pick an auth provider, build the
 * client, render.
 *
 * Firebase is imported dynamically for the same reason as there: it is the
 * largest thing in the bundle by some distance and a development build never
 * touches it, so it is one chunk that only the real sign-in pays for.
 */
async function boot(): Promise<void> {
  const auth: AuthProvider =
    config.auth.mode === 'fake'
      ? new FakeAuthProvider()
      : new (await import('./auth/firebase')).FirebaseAuthProvider(config.auth.firebase);

  const api = new AdminApi({
    baseUrl: config.apiBaseUrl,
    getToken: (forceRefresh) => auth.token(forceRefresh),
  });

  // The two strings that exist before React does. `index.html` ships them in
  // English because a static shell has to ship in *some* language; the browser
  // tab and `documentElement.lang` are corrected here, once, from the same
  // device-resolved locale every component reads (`messages/locale.ts`).
  // `lang` is not decoration — it is what a screen reader picks a voice from.
  document.documentElement.lang = DEVICE_LOCALE;
  document.title = translatorFor(DEVICE_LOCALE)('signin.title');

  const container = document.getElementById('root');
  if (container === null) throw new Error('no #root');
  createRoot(container).render(
    <StrictMode>
      <App console={{ api, auth, config }} />
    </StrictMode>,
  );
}

void boot();
